import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

import { ORB_STIPPLE, buildOrbForm, orbDotCount, seededRandom, type OrbForm } from './orbForms';
import { MOOD_TARGETS, speechLevelAt, stepSpring, type OrbMood, type SpeechPlan, type SpringState } from './orbMotion';
import { renderOrbFrame } from './orbRender';

export type OrbSpeech = { readonly plan: SpeechPlan; readonly startedAt: number };

export type DotOrbHandle = {
  /** A keystroke: the dots ripple up from the text box and the spin picks up. */
  nudge: () => void;
};

type DotOrbProps = {
  size: number;
  mood?: OrbMood;
  form?: OrbForm;
  /** What it is saying right now; the dots swell with each syllable. */
  speech?: OrbSpeech | null;
  /** Read aloud by screen readers; leave empty when a visible name sits beside it. */
  label?: string;
  onDark?: boolean;
  className?: string;
  seed?: number;
  /** Shrinks with its container, never past `size`; the canvas keeps drawing at `size`. */
  fluid?: boolean;
};

const NUDGE = 0.34;
const RIPPLE_DECAY_PER_S = 2.4;
const MORPH_MS = 900;
const TILT = 0.38;
/** Lit side to shadow side: Dale's orange, brightest where the light falls. */
const FALLBACK_INKS = ['#f2a46c', '#ec9055', '#e27a3c', '#d0652a', '#b3531b'];

/**
 * The Dale Agents mascot: Dale's orange dot, made of dots. It idles, listens
 * while you type, dissolves into dust while it thinks and pulses as it talks.
 * Drawn on a canvas, paused off screen and in background tabs, and still for
 * people who asked their system for less motion.
 */
export const DotOrb = forwardRef<DotOrbHandle, DotOrbProps>(function DotOrb(
  { size, mood = 'idle', form = 'lattice', speech = null, label, onDark = false, className, seed = 7, fluid = false },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const count = orbDotCount(size);
  const extras = useMemo(() => pointExtras(count, seed), [count, seed]);
  const live = useRef({ mood, speech, form, ripple: 0 });

  // The loop reads the latest props without restarting the canvas.
  useEffect(() => {
    live.current = { ...live.current, mood, speech, form };
  }, [mood, speech, form]);

  useImperativeHandle(ref, () => ({
    nudge: () => {
      live.current = { ...live.current, ripple: Math.min(1, live.current.ripple + NUDGE) };
    },
  }), []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);
    const inks = readInks(canvas);
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const forms = new Map<OrbForm, Float32Array>();
    const formOf = (name: OrbForm) => {
      const cached = forms.get(name);
      if (cached) return cached;
      const built = buildOrbForm(name, count, seed);
      forms.set(name, built);
      return built;
    };

    let shown = live.current.form;
    let previous = shown;
    let morphStart = -Infinity;
    let rotation = seededRandom(seed)() * Math.PI * 2;
    let spin: SpringState = { value: MOOD_TARGETS[live.current.mood].spin, velocity: 0 };
    let scatter: SpringState = { value: 0, velocity: 0 };
    let voice: SpringState = { value: 0, velocity: 0 };
    let last = performance.now();
    let frameId = 0;
    let visible = true;

    const draw = (now: number) => {
      const dt = Math.max(0, (now - last) / 1000);
      last = now;
      const state = live.current;
      const target = MOOD_TARGETS[state.speech ? 'speaking' : state.mood];
      if (state.form !== shown) {
        previous = shown;
        shown = state.form;
        morphStart = now;
      }
      const level = state.speech ? speechLevelAt(state.speech.plan, now - state.speech.startedAt) : 0;
      spin = stepSpring(spin, target.spin + state.ripple * 0.8, dt);
      scatter = stepSpring(scatter, target.scatter, dt * 0.7);
      voice = stepSpring(voice, level, dt * 2.2);
      rotation += spin.value * dt;
      live.current = { ...state, ripple: state.ripple * Math.exp(-RIPPLE_DECAY_PER_S * dt) };
      const morph = easeInOut(Math.min(1, (now - morphStart) / MORPH_MS));
      renderOrbFrame(ctx, {
        sizePx: size,
        dpr,
        from: formOf(previous),
        to: formOf(shown),
        morph,
        stipple: ORB_STIPPLE[previous] + (ORB_STIPPLE[shown] - ORB_STIPPLE[previous]) * morph,
        ...extras,
        rotation,
        tilt: TILT,
        scatter: Math.max(0, scatter.value),
        breathe: target.breathe,
        ripple: live.current.ripple,
        voice: Math.max(0, voice.value),
        timeS: now / 1000,
        inks,
        onDark,
      });
    };

    const loop = (now: number) => {
      draw(now);
      frameId = visible && !document.hidden ? requestAnimationFrame(loop) : 0;
    };
    const resume = () => {
      if (frameId || reduced || !visible || document.hidden) return;
      last = performance.now();
      frameId = requestAnimationFrame(loop);
    };

    if (reduced) {
      draw(performance.now());
      return undefined;
    }
    const observer = typeof IntersectionObserver === 'function'
      ? new IntersectionObserver(([entry]) => {
          visible = entry?.isIntersecting ?? true;
          resume();
        })
      : null;
    observer?.observe(canvas);
    document.addEventListener('visibilitychange', resume);
    frameId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(frameId);
      observer?.disconnect();
      document.removeEventListener('visibilitychange', resume);
    };
  }, [size, count, seed, extras, onDark]);

  return (
    <canvas
      ref={canvasRef}
      className={className ? `dot-orb ${className}` : 'dot-orb'}
      style={fluid ? { width: '100%', maxWidth: size, aspectRatio: '1 / 1' } : { width: size, height: size }}
      role={label ? 'img' : undefined}
      aria-label={label || undefined}
      aria-hidden={label ? undefined : true}
      data-mood={speech ? 'speaking' : mood}
    />
  );
});

/** Per point: a drift direction for the dissolve, a stipple threshold and a twinkle phase. */
function pointExtras(count: number, seed: number) {
  const random = seededRandom(seed * 31 + 1);
  const drift = new Float32Array(count * 3);
  const thresholds = new Float32Array(count);
  const phases = new Float32Array(count);
  for (let index = 0; index < count; index += 1) {
    const y = random() * 2 - 1;
    const ring = Math.sqrt(1 - y * y);
    const angle = random() * Math.PI * 2;
    const reach = 0.25 + random() * 0.75;
    drift.set([Math.cos(angle) * ring * reach, y * reach, Math.sin(angle) * ring * reach], index * 3);
    thresholds[index] = random();
    phases[index] = random() * Math.PI * 2;
  }
  return { drift, thresholds, phases };
}

/** A look can recolor the dots with `--orb-inks`: a comma-free, space-separated list of colors. */
function readInks(canvas: HTMLCanvasElement): readonly string[] {
  const value = getComputedStyle(canvas).getPropertyValue('--orb-inks').trim();
  const inks = value.split(/\s+/).filter(Boolean);
  return inks.length > 0 ? inks : FALLBACK_INKS;
}

function easeInOut(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}
